import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch,
} from '@mui/material';
import {
    Visibility,
    Download,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { formatNumber } from 'state/helpers';
import { exportToExcel } from 'state/excel';
import { logClientAction } from 'state/logger';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import StyledButton from 'components/StyledButton';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';
import InventoryByProductIdDialog from './InventoryByProductIdDialog';


const ProductInventorySummary = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openInventoryByProductIdDialog, setOpenInventoryByProductIdDialog] = useState(false);
    const [selectedProductName, setSelectedProductName] = useState(false);

    const [productsInventory, setProductsInventory] = useState([]);
    const [inventoryByProductId, setInventoryByProductId] = useState([]);

    const [showSuppliers, setShowSuppliers] = useState(false);

    const handleSwitchChange = (event) => {
        setShowSuppliers(event.target.checked);
    };

    useEffect(() => {
        const handleProductInventory = async () => {
            if (!startDate || !endDate) return [];

            try {
                setError(null);
                setLoading(true);
                const url = '/inflightsales/productinventory';
                const payload = { startDate, endDate, timeMode };
                const res = await api.post(url, payload);
                const result = res.data;

                if (showSuppliers) {
                    setProductsInventory(result);
                    return;
                }

                const FIELDS = [
                    'StockIn', 'PreviousReceipt', 'NextRetrieval', 'StockOn',
                    'Shrinkage', 'Combo', 'Loss', 'Consume'
                ];

                const summaryMap = result.reduce((acc, product) => {
                    const key = `${product.productId}-${product.productName}`;
                    if (!acc[key]) {
                        acc[key] = {
                            productId: product.productId,
                            productName: product.productName,
                            category: product.category,
                            unit: product.unit,
                        };

                        FIELDS.forEach(field => {
                            acc[key][`preorder${field}`] = 0;
                            acc[key][`direct${field}`] = 0;
                        });
                    }

                    FIELDS.forEach(field => {
                        acc[key][`preorder${field}`] += product[`preorder${field}`] || 0;
                        acc[key][`direct${field}`] += product[`direct${field}`] || 0;
                    });

                    return acc;
                }, {});

                const summarizedProducts = Object.values(summaryMap)?.map((product, idx) => {
                    const totalStockIn = (product.preorderStockIn || 0) + (product.directStockIn || 0);
                    const totalPreviousReceipt = (product.preorderPreviousReceipt || 0) + (product.directPreviousReceipt || 0);
                    const totalConsume = (product.preorderConsume || 0) + (product.directConsume || 0);

                    const denominator = totalStockIn + totalPreviousReceipt;
                    const consumeRate = denominator ? ((totalConsume / denominator) * 100).toFixed(0) : 0;
                    const takeBackRate = 100 - Number(consumeRate);

                    return {
                        _id: idx + 1,
                        ...product,
                        consumeRate,
                        takeBackRate,
                    };
                });

                setProductsInventory(summarizedProducts);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        handleProductInventory();
    }, [timeMode, startDate, endDate, showSuppliers]);


    const handleViewClick = useCallback(async (productId) => {
        if (!startDate || !endDate) return [];

        try {
            const url = '/inflightsales/inventorybyproductid';
            const payload = { productId, startDate, endDate, timeMode };
            const res = await api.post(url, payload);

            setInventoryByProductId(res.data);
            setOpenInventoryByProductIdDialog(true);
        } catch (error) {
            toast.error(error || "Failed to fetch inventory by ProductId!");
        }
    }, [timeMode, startDate, endDate]);

    const handleDialogClose = () => setOpenInventoryByProductIdDialog(false);

    const handleExcelJSDownload = () => {
        if (!productsInventory || productsInventory.length === 0) {
            toast.error("No data available to export.");
            return;
        }
        const timestamp = new Date().toISOString().replace(/[-T:.Z]/g, "").slice(0, 14);
        const fileName = `ProductsInventory_${timestamp}.xlsx`;

        const filteredInventory = productsInventory?.map(product => {
            const { productId, ...rest } = product;
            return { ...rest };
        });

        exportToExcel(filteredInventory, fileName);
    };

    const columns = useMemo(() => {
        const commonNumericConfig = {
            headerClassName: 'theme-column--header',
            align: 'right',
            width: 135,
            valueFormatter: (params) => params ? Math.ceil(params).toLocaleString() : ''
        };

        let baseColumns = [
            {
                field: "no",
                headerName: "No",
                headerClassName: 'theme-column--header',
                filterable: false,
                sortable: false,
                width: 60,
                renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
            },
            {
                field: "productName",
                headerName: "ProductName",
                headerClassName: 'theme-column--header',
                width: 300
            },
            {
                field: "category",
                headerName: "Category",
                headerClassName: 'theme-column--header',
                width: 100
            },
            {
                field: "unit",
                headerName: "Unit",
                headerClassName: 'theme-column--header',
                width: 100
            },
            ...["preorder", "direct"].flatMap(prefix =>
                ["StockIn", "PreviousReceipt", "NextRetrieval", "StockOn", "Shrinkage", "Combo", "Loss", "Consume"]?.map(field => ({
                    field: `${prefix}${field}`,
                    headerName: `${prefix.charAt(0).toUpperCase() + prefix.slice(1)}${field}`,
                    ...commonNumericConfig
                }))
            ),
            { field: "consumeRate", headerName: "ConsumeRate (%)", ...commonNumericConfig },
            { field: "takeBackRate", headerName: "TakeBackRate (%)", ...commonNumericConfig },
        ];

        if (showSuppliers) {
            baseColumns.splice(1, 0, {
                field: "supplierName",
                headerName: "SupplierName",
                headerClassName: 'theme-column--header',
                width: 150
            });
        }

        return baseColumns;
    }, [showSuppliers]);

    const actionColumn = useMemo(() => [{
        field: "action",
        headerName: "Action",
        width: 80,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                gap={1}
                sx={{ width: "100%", height: "100%" }}
            >
                <Tooltip title="View">
                    <IconButton
                        aria-label="view"
                        sx={{
                            color: theme.palette.primary.dark,
                            "&:hover": { color: theme.palette.green.deep },
                        }}
                        onClick={() => {
                            setSelectedProductName(params?.row?.productName);
                            handleViewClick(params?.row?.productId);
                        }}
                    >
                        <Visibility />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    }], [theme, handleViewClick]);


    const cardFields = useMemo(() => [
        ...(showSuppliers ? [{ label: "Supplier", key: "supplierName" }] : []),
        { label: "Category", key: "category" },
        { label: "Unit", key: "unit" },
        { label: "Preorder StockIn",  key: "preorderStockIn",  cols: 2, render: (r) => formatNumber(r.preorderStockIn) },
        { label: "Direct StockIn",    key: "directStockIn",    cols: 2, render: (r) => formatNumber(r.directStockIn) },
        { label: "Preorder Consume",  key: "preorderConsume",  cols: 2, render: (r) => formatNumber(r.preorderConsume) },
        { label: "Direct Consume",    key: "directConsume",    cols: 2, render: (r) => formatNumber(r.directConsume) },
        { label: "Consume Rate",      key: "consumeRate",      cols: 2, render: (r) => r.consumeRate != null ? `${r.consumeRate}%` : null },
        { label: "TakeBack Rate",     key: "takeBackRate",     cols: 2, render: (r) => r.takeBackRate != null ? `${r.takeBackRate}%` : null },
    ], [showSuppliers]);


    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header
                    title="Inventory Summary"
                    subtitle={`${loading ? 'Loading...' : 'List of Inventory Products'}`}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showSuppliers}
                                    onChange={handleSwitchChange}
                                />
                            }
                            label="Suppliers"
                        />
                        <Tooltip title="Download Excel file" arrow>
                            <span>
                                <StyledButton
                                    onClick={() => {
                                        handleExcelJSDownload();
                                        logClientAction("EXPORT_REPORT", { fileName: "ProductsInventorySummary", format: "Excel", startDate, endDate });
                                    }}
                                >
                                    <Download />
                                </StyledButton>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    gap={2}
                >
                    <Box
                        height={LAYOUT_HEIGHT}
                        sx={{ gridColumn: "span 12" }}
                    >
                        {isMobile ? (
                            <CardList
                                rows={productsInventory}
                                primaryField="productName"
                                secondaryField="category"
                                minWidth={105}
                                fields={cardFields}
                                isActiveField={null}
                                canView
                                canEdit={false}
                                canDelete={false}
                                onView={(id) => {
                                    const item = productsInventory.find(p => String(p._id) === String(id));
                                    if (item) {
                                        setSelectedProductName(item.productName);
                                        handleViewClick(item.productId);
                                    }
                                }}
                            />
                        ) : (
                            <DataGridComponent
                                rows={productsInventory}
                                columns={[...columns, ...actionColumn]}
                                loading={loading}
                            />
                        )}
                    </Box>
                </Box>
            )}

            {openInventoryByProductIdDialog &&
                <InventoryByProductIdDialog
                    open={openInventoryByProductIdDialog}
                    onClose={handleDialogClose}
                    selectedProductName={selectedProductName}
                    inventoryProducts={inventoryByProductId}
                />
            }
        </Box>
    );
};

export default ProductInventorySummary;
